import {
  type AnyRouteMatch,
  type AnyRouter,
  type LinkProps,
  type ParsedLocation,
  notFound,
} from '@tanstack/react-router';
import decamelize from 'decamelize';
import { z, type ZodType } from 'zod';
import { type SentryDomain } from '@/api/errors';
import { type ClusterTemplateType } from '@/components/Clusters/ClusterSetup/ClusterTemplateList/types';
import { MIN_NODES } from '@/components/Clusters/ClusterSetup/helpers/constants';
import { CLOUD_PROVIDER_MAP, CLOUD_PROVIDERS } from '@/components/Clusters/constants';

// Re-export auth schemas for backwards compatibility (moved to break circular dependency)
export {
  loginSearchSchema,
  logoutSearchSchema,
  type LoginSearchSchema,
  type LogoutSearchSchema,
} from '@/router/helpers/auth-schemas';

// `serverless` creates a space, not a cluster: the create page offers all three side by side.
const TEMPLATE_TYPES = ['free', 'standard', 'serverless'] as const satisfies readonly (
  | ClusterTemplateType
  | 'serverless'
)[];

export const clusterSetupSearchSchema = z.object({
  pid: z.string().optional(),
  nodes: z.coerce.number().min(MIN_NODES).optional().catch(MIN_NODES),
  disk: z.coerce.number().min(0).optional().catch(0),
});

export type ClusterSetupSearchParams = z.infer<typeof clusterSetupSearchSchema>;

export const clusterCreateBaseSearchSchema = z.object({
  provider: z.enum([CLOUD_PROVIDER_MAP.AWS, CLOUD_PROVIDER_MAP.GCP, CLOUD_PROVIDER_MAP.AZURE]).optional(),
  region: z.string().optional(),
});

export type ClusterCreateBaseSearchParams = z.infer<typeof clusterCreateBaseSearchSchema>;

export const clusterListSearchSchema = clusterCreateBaseSearchSchema.extend({
  view: z.enum(['table', 'grid']).optional().catch(undefined),
});

export type ClusterListSearchParams = z.infer<typeof clusterListSearchSchema>;

export const clusterCreateSearchSchema = clusterCreateBaseSearchSchema.extend({
  provider: z.enum(CLOUD_PROVIDERS).optional(),
  name: z.coerce.string().optional(),
  template: z.enum(TEMPLATE_TYPES).default(TEMPLATE_TYPES[1]).catch(TEMPLATE_TYPES[1]),
});

export type ClusterCreateSearchParams = z.infer<typeof clusterCreateSearchSchema>;

const clusterCreateSearchDefaultParams: Partial<ClusterCreateSearchParams> = {
  template: 'standard',
};

/**
 * Parses and validates the provided search parameters for cluster creation.
 *
 * @param {string} [params.template='standard'] - The template type for the cluster. Defaults to 'standard'.
 */
export function clusterCreateSearch(params = clusterCreateSearchDefaultParams): ClusterCreateSearchParams {
  return clusterCreateSearchSchema.parse({ ...clusterCreateSearchDefaultParams, ...params });
}

export const utmParamsSchema = z.object({
  /**
   * gbraid (Google Braid) and wbraid (Web Braid): two new parameters designed to measure ad performance while respecting privacy policies.
   * Google unique identifier to each ad click. wbraid: Web-to-App; gbraid: App-to-App.
   */
  gbraid: z.string().optional(),
  wbraid: z.string().optional(),

  /**
   * Google click identifier.
   */
  gclid: z.string().optional(),

  /**
   * The name of the campaign that defines a particular marketing context
   */
  utm_campaign: z.coerce.string().optional(),
  /**
   * Identifies what specifically was clicked to bring the user to the site, such as a banner ad or a text link
   */
  utm_content: z.coerce.string().optional(),
  /**
   * The advertising or marketing media used to reach your site
   */
  utm_medium: z.coerce.string().optional(),
  /**
   * The site (source), where your user is coming from
   */
  utm_source: z.coerce.string().optional(),
  /**
   * Primarily used in Google AdWords campaigns, this parameter informs you which terms were used in search ads, such as “shorts sale” or “deal on shirts.”
   */
  utm_term: z.coerce.string().optional(),
});

export type UtmSearchParams = z.infer<typeof utmParamsSchema>;

export const baseHubspotParamsSchema = z.object({
  /**
   * Hubspot crossdomain tracking parameter (1 of 3)
   */
  __hstc: z.coerce.string().optional(),
  /**
   * Hubspot crossdomain tracking parameter (2 of 3)
   */
  __hssc: z.coerce.string().optional(),
  /**
   * Hubspot crossdomain tracking parameter (3 of 3)
   */
  __hsfp: z.coerce.string().optional(),
});

export const hubspotParamsSchema = baseHubspotParamsSchema.superRefine((data, ctx) => {
  if (data.__hstc) {
    const parts = data.__hstc.split('.');
    if (parts.length > 1 && parts[1].length !== 32) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid __hstc format: The second part (tracking ID) must be 32 characters long.',
        path: ['__hstc'],
      });
    }
  }
});

export type BaseHubspotSearchParams = z.infer<typeof baseHubspotParamsSchema>;

export const segmentParamsSchema = z.object({
  /**
   * Segment crossdomain tracking parameter
   */
  qdrant_tech_ajs_anonymous_id: z.coerce.string().optional(),
});

export const onetrustParamsSchema = z.object({
  /**
   * OneTrust crossdomain preferences parameter
   */
  qdrant_tech_ot_consent: z.coerce.string().optional(),
});

/**
 * Suger entitlement schema for cookie parsing
 */
export const sugerEntitlementSchema = z.object({
  sugerEntitlementId: z.string(),
  partner: z.enum(['aws', 'gcp', 'azure']).optional(),
});

export type SugerEntitlementSearchParams = z.infer<typeof sugerEntitlementSchema>;
export type MarketplaceProvider = NonNullable<SugerEntitlementSearchParams['partner']>;

export const rootSearchSchema = sugerEntitlementSchema
  .partial()
  .extend(utmParamsSchema.shape)
  .extend(baseHubspotParamsSchema.shape)
  .extend(segmentParamsSchema.shape)
  .extend(onetrustParamsSchema.shape);

export const authenticatedSearchSchema = z.object({
  /**
   * Account ID to set as active in the account navigation UI.
   */
  account_id: z.guid().optional(),
  /**
   * Authentication error from the dashboard UI.
   */
  cluster_ui_aerr: z.enum(['expired', 'invalid']).optional(),
});

export const accountSearchSchema = z.object({
  /**
   * Stripe Session ID.
   */
  session_id: z.string().optional(),
  /**
   * Indicates the user has linked their login during the authentication callback flow.
   */
  login_linked: z.boolean().optional(),
});

export const hybridCloudSearchSchema = z.object({
  showCleanUpCommands: z.literal(true).optional().catch(undefined),
});

/**
 * Parses and validates the provided path parameters for the route. If it fails, a notFound error is thrown
 * and route execution will be skipped.
 *
 * Example usage:
 *
 * ```ts
 *   path: '$clusterId',
 *   params: validateParams(
 *    z.object({
 *       clusterId: z.guid()
 *     })
 *    ),
 *  }
 * ```
 */
export const validateParams = <TParams extends Partial<Record<string, unknown>>>(schema: ZodType<TParams>) => ({
  parse: (params: Record<string, string>) => {
    const result = schema.safeParse(params);
    if (result.success) {
      return result.data;
    }
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw notFound({ data: { cause: result.error } });
  },
  stringify: (_: Required<TParams>) => _,
});

export function routeToUrlTemplate(to: NonNullable<LinkProps['to']>) {
  return decamelize(to.replace(/\$([^/]+)/g, '{$1}')) as `/${string}`;
}

/**
 * The route matches the user is on right now. `state.matches` only commits once the loaders settle, so
 * during a load it is empty (initial load) or still points at the route the user came *from*
 * (navigation) — and loader errors are read from here before that commit. `state.location` already
 * holds the destination, so match against it while a load is in flight.
 */
function getCurrentMatches(router: AnyRouter): AnyRouteMatch[] {
  if (!router.state.isLoading) {
    return router.state.matches;
  }
  return router.matchRoutes(router.state.location as ParsedLocation);
}

/**
 * The route the user is on right now. See {@link getCurrentMatches} for why an in-flight load matches
 * the location instead.
 */
export function getCurrentRouteId(router: AnyRouter): string | undefined {
  const routeId: unknown = getCurrentMatches(router).at(-1)?.routeId;
  return typeof routeId === 'string' ? routeId : undefined;
}

/**
 * The {@link SentryDomain} that owns the current route, read from `staticData.domain`. staticData is
 * per-route, so we walk the matched routes from the leaf up and return the nearest annotated value —
 * giving subtree inheritance (annotate a subtree root, children inherit) and override (a child sets
 * its own). Used as the `domain` fallback for events with no API-derived domain, see `beforeSend`.
 */
export function getCurrentRouteDomain(router: AnyRouter): SentryDomain | undefined {
  return getCurrentMatches(router).findLast((match) => match.staticData?.domain)?.staticData?.domain;
}
